"use client";

import { useEffect, useId, useRef, useState } from "react";
import Image from "next/image";
import { Modal } from "../../components/Modal";
import { AddToCartPanel } from "./AddToCartPanel";
import { Product, formatPrice } from "../../data/products";
import { useCart } from "@/lib/cart-store";

type Props = {
  product: Product;
  /** DOM id of the in-page AddToCartPanel container to observe. */
  watchElementId: string;
};

/**
 * StickyBuyBar — appears once the main AddToCartPanel scrolls out of view.
 *
 * Mobile: fixed bottom-of-viewport pill bar with safe-area inset.
 * Desktop: centered max-w-2xl rounded-full bar floating above content.
 *
 * "Add to bag" opens the existing AddToCartPanel inside a Modal so the user
 * sees the same size picker they would on the PDP — no parallel UI to drift.
 */
export function StickyBuyBar({ product, watchElementId }: Props) {
  const [showBar, setShowBar] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const titleId = useId();
  const observed = useRef<Element | null>(null);
  const { drawerOpen } = useCart();

  useEffect(() => {
    const target = document.getElementById(watchElementId);
    if (!target) return;
    observed.current = target;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show the bar when the panel is NOT intersecting the viewport.
        setShowBar(!entry.isIntersecting);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0 }
    );
    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [watchElementId]);

  const productLabel = `${product.brand} ${product.model} — ${product.colorway}`;
  // Hide the bar whenever the cart drawer is open — keeps a single primary
  // surface visible at a time, and prevents the buy-bar Modal from stacking
  // on top of the drawer if the user taps it mid-drawer-animation.
  const visible = showBar && !drawerOpen;

  return (
    <>
      <div
        aria-hidden={!visible}
        className={[
          "pointer-events-none fixed inset-x-0 bottom-0 z-40 px-2 transition-all duration-200 md:bottom-6 md:px-6",
          visible
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0 md:translate-y-6",
        ].join(" ")}
      >
        <div
          className={[
            "pointer-events-auto mx-auto flex items-center gap-3 border border-[var(--color-bone)]/15 bg-[var(--color-ink)]/95 px-3 py-2 shadow-2xl backdrop-blur",
            "pb-[max(env(safe-area-inset-bottom),0.5rem)]",
            "md:max-w-2xl md:rounded-full md:px-3 md:py-2 md:pb-2",
          ].join(" ")}
        >
          <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-sm bg-white md:rounded-full">
            {product.imageSrc ? (
              <Image
                src={product.imageSrc}
                alt=""
                fill
                sizes="48px"
                className="object-contain p-1"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--color-storm)]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="mono-strip truncate text-[9px] text-[var(--color-foil)]">
              {product.brand.toUpperCase()}
            </div>
            <div className="font-display truncate text-sm uppercase leading-tight text-[var(--color-bone)]">
              {product.colorway}
            </div>
          </div>
          <div className="hidden font-mono text-sm text-[var(--color-bone)] sm:block">
            {formatPrice(product.priceCents)}
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label={`Add ${productLabel} to bag`}
            className="mono-strip inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-blood)] px-4 py-2.5 text-[10px] text-white transition hover:bg-[var(--color-blood)]/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)]"
          >
            Add to bag
            <ChevronUp className="h-3 w-3" />
          </button>
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        labelledBy={titleId}
        size="sm"
      >
        <div className="flex items-start gap-4 border-b border-[var(--color-bone)]/10 p-5">
          <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden bg-white">
            {product.imageSrc && (
              <Image
                src={product.imageSrc}
                alt=""
                fill
                sizes="80px"
                className="object-contain p-2"
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="mono-strip text-[10px] text-[var(--color-foil)]">
              {product.brand.toUpperCase()} · {product.sku}
            </div>
            <h2
              id={titleId}
              className="font-display mt-1 text-lg uppercase leading-tight"
            >
              {product.model}
            </h2>
            <div className="font-mono text-xs text-[var(--color-bone-dim)]">
              {product.colorway}
            </div>
            <div className="mt-2 font-mono text-base text-[var(--color-bone)]">
              {formatPrice(product.priceCents)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            aria-label="Close size picker"
            className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-bone-dim)] transition hover:bg-[var(--color-bone)]/10 hover:text-[var(--color-bone)]"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">
          <AddToCartPanel
            product={product}
            onAdded={() => setModalOpen(false)}
          />
        </div>
      </Modal>
    </>
  );
}

function ChevronUp({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 15l6-6 6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
